import type { RequiringAction } from '@paid-ui/types';
import type { SortByDirection } from '@paid-ui/types/sort';

import { useSearchParams } from 'next/navigation';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { ContractType } from '@paid-ui/enums/contract';
import { ProjectStatus } from '@paid-ui/enums/project';
import { parseSortDirection } from '@paid-ui/types/sort';
import { useToast } from '@paid-ui/ui/toast';
import { request } from '@paid-ui/utils/axios';

export type SortByKey = 'CREATED_AT' | 'PROJECT_NAME';
export type { SortByDirection } from '@paid-ui/types/sort';

export interface PageSettings {
  page: number;
  size: number;
  sortByKey: SortByKey;
  sortByDirection: SortByDirection;
}

export interface SearchProjectsBody {
  searchTerm?: string;
  onlyExecuted?: boolean;
  noHeadBuildingContracts?: boolean;
  projectStatus?: ProjectStatus;
  pageSettings: PageSettings;
}

export interface ProjectSummary {
  contractCounters: {
    executedContracts: number;
    fundingContracts: number;
    headContracts: number;
    payeeContracts: number;
    payerContracts: number;
    serviceContracts: number;
    subContracts: number;
    supplyContracts: number;
  };
  createdAt: string;
  customName?: string;
  formattedAddress: string;
  group: {
    id: string;
  };
  id: string;
  projectNumber?: string;
  projectStatus: ProjectStatus;
  projectActions?: RequiringAction[];
  totalClaimsAmountAsPayer?: number;
  totalClaimsPaid?: number;
  totalClaimsAmountAsPayee?: number;
  totalClaimsReceived?: number;
}

export interface SearchProjectsResponse {
  data: {
    content: ProjectSummary[];
    actualPageSize: number;
    currentPage: number;
    requestedPageSize: number;
    total: number;
    totalPages: number;
  };
}

interface Props {
  keyword?: string;
  onlyExecuted?: boolean;
  contractType?: ContractType | null;
  enabled?: boolean;
}

const defaultPageSize = 20;

export const useSearchProjects = (props: Props) => {
  const toast = useToast();
  const { keyword, onlyExecuted = false, contractType, enabled = true } = props;

  return useInfiniteQuery({
    keepPreviousData: true,
    enabled,
    queryKey: ['SearchProjects', keyword, onlyExecuted, contractType],
    queryFn: async ({ signal, pageParam = 0 }) => {
      const body: SearchProjectsBody = {
        onlyExecuted,
        noHeadBuildingContracts: contractType === ContractType.HEAD_CONTRACT,
        projectStatus: ProjectStatus.ACTIVE,
        pageSettings: {
          size: defaultPageSize,
          page: pageParam,
          sortByKey: 'CREATED_AT',
          sortByDirection: 'DESC',
        },
      };

      if (keyword) {
        body.searchTerm = keyword;
      }

      const { data } = await request.post<SearchProjectsResponse>('/projects/search', body, {
        signal,
      });

      return {
        data: data?.data.content ?? [],
        pageSize: data?.data?.requestedPageSize ?? defaultPageSize,
        currentPage: data?.data?.currentPage ?? 0,
        total: data?.data?.total ?? 0,
        totalPages: data?.data?.totalPages ?? 0,
      };
    },
    getNextPageParam: (lastPage) => {
      const { currentPage, totalPages } = lastPage;
      return currentPage < Math.max(0, totalPages - 1) ? currentPage + 1 : null;
    },
    onError(err) {
      toast.error(err instanceof Error ? err.message : `Failed to search projects.`);
    },
  });
};

const VALID_SORT_KEYS = new Set<SortByKey>(['CREATED_AT', 'PROJECT_NAME']);

const tablePageSize = 10;

interface GetProjectsOptions {
  /**
   * Page size to use when the URL carries no `size`. The projects table wants ten
   * rows; the dashboard wants a multiple of its three-column grid.
   */
  defaultSize?: number;
}

export const useGetProjects = ({ defaultSize = tablePageSize }: GetProjectsOptions = {}) => {
  const toast = useToast();
  const searchParams = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') || '0');
  const size = Number.parseInt(searchParams.get('size') || String(defaultSize));
  const searchTerm = searchParams.get('searchTerm') || '';
  const contractType = searchParams.get('contractType') || '';
  const projectStatus = searchParams.get('projectStatus') || 'ACTIVE';
  const onlyExecuted = searchParams.get('onlyExecuted') || 'false';
  const sortByKeyParam = searchParams.get('sortByKey');
  const sortByDirectionParam = searchParams.get('sortByDirection');
  const sortByKey: SortByKey =
    sortByKeyParam && VALID_SORT_KEYS.has(sortByKeyParam as SortByKey)
      ? (sortByKeyParam as SortByKey)
      : 'CREATED_AT';
  const sortByDirection = parseSortDirection(sortByDirectionParam);

  const query = useQuery({
    keepPreviousData: true,
    queryKey: [
      'GetProjects',
      page,
      size,
      searchTerm,
      onlyExecuted,
      contractType,
      projectStatus,
      sortByKey,
      sortByDirection,
    ],
    queryFn: async ({ signal }) => {
      const body: SearchProjectsBody = {
        onlyExecuted: onlyExecuted === 'true',
        noHeadBuildingContracts: contractType === ContractType.HEAD_CONTRACT,
        pageSettings: {
          size: size,
          page: page,
          sortByKey,
          sortByDirection,
        },
      };

      if (searchTerm) {
        body.searchTerm = searchTerm;
      }

      if (projectStatus === 'ACTIVE') {
        body.projectStatus = ProjectStatus.ACTIVE;
      } else {
        body.projectStatus = ProjectStatus.ARCHIVED;
      }

      const { data } = await request.post<SearchProjectsResponse>('/projects/search', body, {
        signal,
      });

      return {
        data: data?.data.content ?? [],
        pageSize: data?.data?.requestedPageSize ?? defaultPageSize,
        currentPage: data?.data?.currentPage ?? 0,
        total: data?.data?.total ?? 0,
        totalPages: data?.data?.totalPages ?? 0,
      };
    },
    onError(err) {
      toast.error(err instanceof Error ? err.message : 'Failed to get projects.');
    },
  });

  return query;
};
